import { Component, Input, TemplateRef } from '@angular/core';
import { NgIf } from '@angular/common';
import { Device } from '@capacitor/device';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { NotificationsComponent } from '../../notifications/notifications.component';
import { SyncStatusService } from '../../../../../shared/services/sync-status.service';
import { AuthService } from '../../../../../shared/services/auth.service';
import { QrCodeComponent } from '../qr-code/qr-code.component';

@Component({
    selector: 'app-user-nav',
    templateUrl: './user-nav.component.html',
    styleUrls: ['./user-nav.component.css'],
    standalone: true,
    imports: [
        NgIf,
        RouterLink,
        TranslateModule,
        NotificationsComponent,
        QrCodeComponent
    ],
    providers: [
        BsModalService
    ]
})
export class UserNavComponent {

    @Input() userPhoto: string = '';
    @Input() userName: string = '';
    @Input() userId: number;

    public appVersion: string;
    public appHash: string;
    public appBuildDate: string;
    public currentYear: number = new Date().getFullYear();
    public deviceInfo: any = {
        model: '',
        platform: '',
        version: '',
        manufacturer: '',
        isVirtual: false,
        osVersion: '',
        webViewVersion: '',
        isPWA: false,
        battery: {
            batteryLevel: 0,
            isCharging: false
        },
        id: {
            identifier: ''
        }
    };

    private modalRef?: BsModalRef;

    constructor(
        public syncStatusService: SyncStatusService,
        private authService: AuthService,
        private toastrService: ToastrService,
        private router: Router,
        private modalService: BsModalService,
    ) {}

    public logout() {
        this.authService.logout().subscribe(
            () => {
                this.authService.removeStorageCurrentUser();
                this.router.navigate([ '/login' ]);
                this.toastrService.success('Logout realizado com sucesso!');
            },
            error => {
                this.authService.removeStorageCurrentUser();
                this.router.navigate([ '/login' ]);
                console.log(error);
            });
    }

    public async showAboutModal(template: TemplateRef<any>): Promise<void> {

        this.appVersion = '<i class="fa fa-spinner fa-spin"></i>';
        this.appHash = '<i class="fa fa-spinner fa-spin"></i>';
        this.appBuildDate = '<i class="fa fa-spinner fa-spin"></i>';

        this.modalRef = this.modalService.show(template, {
            class: 'modal-md',
            keyboard: true,
            backdrop: 'static'
        });

        // Reset deviceInfo para valores padrão
        this.deviceInfo = {
            model: '',
            platform: '',
            version: '',
            manufacturer: '',
            isVirtual: false,
            osVersion: '',
            webViewVersion: '',
            isPWA: false,
            battery: {
                batteryLevel: 0,
                isCharging: false
            },
            id: {
                identifier: ''
            }
        };

        // Carrega informações do dispositivo (tratamento individual para cada API)
        try {
            const info = await Device.getInfo();
            if (info) {
                this.deviceInfo.model = info.model || '';
                this.deviceInfo.platform = info.platform || '';
                this.deviceInfo.manufacturer = info.manufacturer || '';
                this.deviceInfo.isVirtual = info.isVirtual || false;
                this.deviceInfo.osVersion = info.osVersion || '';
                this.deviceInfo.webViewVersion = info.webViewVersion || '';
            }
        } catch (error) {
            console.warn('Erro ao obter Device.getInfo():', error);
            // Fallback para APIs web nativas
            this.deviceInfo.platform = this.getPlatformFallback();
        }

        try {
            const battery = await Device.getBatteryInfo();
            if (battery) {
                this.deviceInfo.battery = {
                    batteryLevel: battery.batteryLevel || 0,
                    isCharging: battery.isCharging || false
                };
            }
        } catch (error) {
            console.warn('Erro ao obter Device.getBatteryInfo():', error);
            // Fallback para Battery API web (se disponível)
            this.loadBatteryFallback();
        }

        try {
            const id = await Device.getId();
            if (id && id.identifier) {
                this.deviceInfo.id = {
                    identifier: id.identifier
                };
            }
        } catch (error) {
            console.warn('Erro ao obter Device.getId():', error);
        }

        // Detecta PWA
        this.deviceInfo.isPWA = window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone ||
            document.referrer.includes('android-app://');

        // Carrega informações de versão
        try {
            const response = await fetch('/version.json?t=' + new Date().getTime());
            if (!response.ok) {
                throw new Error('Failed to fetch version.json');
            }
            const data = await response.json();
            
            if (data) {
                this.appVersion = data.version || '-';
                this.appHash = data.hash || '-';
                if (data.buildDate) {
                    try {
                        this.appBuildDate = new Date(data.buildDate).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        });
                    } catch {
                        this.appBuildDate = data.buildDate || '-';
                    }
                } else {
                    this.appBuildDate = '-';
                }
            }
        } catch (error) {
            console.error('Erro ao carregar version.json:', error);
            this.appVersion = '-';
            this.appHash = '-';
            this.appBuildDate = '-';
        }
    }

    private getPlatformFallback(): string {
        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
        
        if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
            return 'ios';
        }
        if (/android/i.test(userAgent)) {
            return 'android';
        }
        if (/win/i.test(userAgent)) {
            return 'windows';
        }
        if (/mac/i.test(userAgent)) {
            return 'mac';
        }
        if (/linux/i.test(userAgent)) {
            return 'linux';
        }
        
        return navigator.platform || 'unknown';
    }

    private async loadBatteryFallback(): Promise<void> {
        try {
            if ('getBattery' in navigator) {
                const battery = await (navigator as any).getBattery();
                if (battery) {
                    this.deviceInfo.battery = {
                        batteryLevel: battery.level || 0,
                        isCharging: battery.charging || false
                    };
                }
            }
        } catch (error) {
            // Battery API não disponível ou falhou
            console.warn('Battery API não disponível:', error);
        }
    }

    public hideModal(): void {
        if (this.modalRef) {
            this.modalRef.hide();
        }
    }
}
